<template>
  <div
    v-if="visible"
    class="fixed top-header left-main m-top-10 bg-normal overlay-modal"
    @click.self="close"
  >
    <div class="modal-content f-col fade-in-blur w-lay-1L bg-normal">
      <div v-if="auth.user && member">
        <form @submit.prevent="save" class="f-col w-100">
          <!-- Email (Read-only) -->
          <div class="readonly-field input-style relative">
            <label>Email</label>
            <span>{{ auth.user.email }}</span>
            <span class="icon-container">
              <LockIcon size="18" :stroke-width="1.5" />
            </span>
          </div>

          <!-- Submission (Read-only) -->
          <div class="readonly-field input-style relative">
            <label>Mentioned in submission</label>
            <span>{{ member.submission ? 'Yes' : 'No' }}</span>
            <span class="icon-container">
              <LockIcon size="18" :stroke-width="1.5" />
            </span>
          </div>

          <!-- Name -->
          <div class="relative block-i w-100">
            <label for="name">Name</label>
            <input
              id="name"
              class="textUpper text700"
              v-model="member.name"
              type="text"
            />
          </div>

          <!-- Website -->
          <div class="relative block-i w-100">
            <label for="web">Website or Social</label>
            <input id="web" v-model="member.web" type="text" />
          </div>

          <!-- Language -->
          <div class="relative block-i w-100">
            <label for="language">Language</label>
            <select id="language" v-model="member.language">
              <option disabled value=""></option>
              <option v-for="lang in languages" :key="lang.id" :value="lang.id">
                {{ lang.label }}
              </option>
            </select>
            <span class="icon-container no-events">
              <ChevronDown size="18" :stroke-width="1.5" class="no-events" />
            </span>
          </div>

          <!-- Type -->
          <div class="relative block-i w-100">
            <label for="type">Type</label>
            <select id="type" v-model="member.type">
              <option disabled value=""></option>
              <option v-for="type in types" :key="type.id" :value="type.id">
                {{ type.label }}
              </option>
            </select>
            <span class="icon-container no-events">
              <ChevronDown size="18" :stroke-width="1.5" class="no-events" />
            </span>
          </div>

          <!-- Interest -->
          <div class="relative block-i w-100">
            <label for="interest">Interest</label>
            <select id="interest" v-model="member.interest">
              <option disabled value=""></option>
              <option v-for="int in interests" :key="int.id" :value="int.id">
                {{ int.label }}
              </option>
            </select>
            <span class="icon-container no-events">
              <ChevronDown size="18" :stroke-width="1.5" class="no-events" />
            </span>
          </div>

          <!-- Status (Sharing) -->
          <div class="f-col gap-1 relative">
            <div class="relative block-i w-100">
              <label for="sharing">Status</label>
              <span class="tooltip-container">
                <InfoIcon size="12" :stroke-width="1.5" />
                <span class="tooltip-text textUpper">
                  when shared, others with a profile may see that you are signed
                  up here.
                </span>
              </span>
              <select id="sharing" v-model="member.sharing">
                <option disabled value=""></option>
                <option :value="true">Share</option>
                <option :value="false">Hide</option>
              </select>
              <span class="icon-container no-events">
                <ChevronDown size="18" :stroke-width="1.5" class="no-events" />
              </span>
            </div>
          </div>

          <div class="f-row gap-1 w-100 m-top-2 just-between">
            <button
              type="submit"
              class="button-style btn-add2 radius-0 tr-all-03 just-c-c"
            >
              Save
            </button>
            <!--<button
              type="button"
              @click="sendPasswordReset(auth.user.email)"
              class="button-style btn-add3 textUpper"
            >
              Reset Password
            </button>
            -->
            <button
              type="button"
              @click="logout"
              class="button-style btn-add3 textUpper op-50-hover tr-all-03"
            >
              LogOut
            </button>
          </div>
        </form>
      </div>
    </div>
    <Transition name="fade">
      <div class="w-lay-1L" v-if="toastMessage">
        <div class="toast-message btn-add4 m-top-2 m-left-main w-fit z-1000">
          {{ toastMessage }}
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
// src/components/navigation/ModalProfile.vue
import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LockIcon from '@/components/global/icons/LockIcon.vue'
import { usePocketAuthStore } from '@/stores/usePocketAuthStore'
import { useToast } from '@/composables/useToast'
import { usePasswordReset } from '@/composables/usePocketPasswordReset'
import { useMemberProfile } from '@/composables/usePocketMemberProfile'
import { useUiState } from '@/stores/useUiState'
import { InfoIcon } from 'lucide-vue-next'
import { ChevronDown } from 'lucide-vue-next'

const props = defineProps({ visible: Boolean })
const emit = defineEmits(['close'])
const ui = useUiState()
const auth = usePocketAuthStore()
const member = computed(() => auth.member)
const router = useRouter()

const { toastMessage, showToast } = useToast()
const { sendPasswordReset } = usePasswordReset()
const {
  loading,
  error,
  languages,
  types,
  interests,
  fetchCurrentMember,
  updateMember,
} = useMemberProfile()

const save = async () => {
  try {
    await updateMember({ ...member.value })
    showToast('Profile saved!')
  } catch (e) {
    showToast(e.message || 'Failed to save')
  }
}

const logout = async () => {
  await auth.logout()
  ui.showLogin = false // ✅ close modal explicitly
  emit('close')
  router.push('/')
}

const close = () => emit('close')
const { locale } = useI18n()
watch(locale, fetchCurrentMember)

watch(
  () => props.visible,
  async (isVisible) => {
    if (isVisible && auth.user) {
      await fetchCurrentMember()
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.icon-container {
  display: flex;
  position: absolute;
  right: 1rem;
  top: 1rem;
}

.tooltip-container {
  display: flex;
  position: absolute;
  left: 4rem;
  top: 0.45rem;
  z-index: 2000;
}

.info-icon {
  cursor: pointer;
  opacity: 0.6;
}

.tooltip-text {
  visibility: hidden;
  width: 220px;
  background-color: var(--base-color10);
  color: currentColor;
  text-align: left;
  border-radius: 4px;
  padding: 0.5rem;
  position: absolute;
  z-index: 10;
  bottom: 0%;
  left: 1.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  font-size: var(--font-size-XS);
  font-family: monospace;
  line-height: 1.2;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
</style>
